import { crossrefIso6393Codes } from '@/__generated__/crossref-language-codes';
import ISO6391 from 'iso-639-1';
import { iso6393, iso6393To1 } from 'iso-639-3';

// --- Allowed codes (from Crossref languages5.4.0.xsd) -------------------------

/** Max suggestions shown in the language autocomplete. */
export const CROSSREF_LANGUAGE_SUGGESTION_LIMIT = 10;

export type CrossrefLanguageOption = {
  code: string;
  name: string;
  nativeName: string;
};

export type CrossrefLanguageListItem = CrossrefLanguageOption & {
  /** Localized primary line, e.g. "Spanisch (spa)". */
  label: string;
  /** Native endonym line, e.g. "Español". */
  nativeLabel: string;
};

/** Legacy Fluent keys (2-letter) used until 3-letter entries exist in locale files. */
const LEGACY_I18N_PREFIX = 'iso-639-1_language_codes';

const TWO_LETTER_SUFFIX = /\s*\([a-z]{2}\)\s*$/i;

/**
 * Localized label for the autocomplete (e.g. "Spanisch (spa)" in German).
 * Reuses existing iso-639-1 FTL strings where possible.
 */
export function formatCrossrefLanguageLabel(
  code: string,
  englishName: string,
  translate: (key: string, defaultMessage?: string) => string | undefined
): string {
  const iso1 = iso6393To1[code];
  if (iso1) {
    const legacyKey = `${LEGACY_I18N_PREFIX}.${iso1}`;
    const translated = translate(legacyKey, undefined);
    if (translated) {
      return translated.replace(TWO_LETTER_SUFFIX, ` (${code})`);
    }
  }

  const crossrefKey = `crossref_language_codes.${code}`;
  const translated = translate(crossrefKey, undefined);
  if (translated) {
    return translated;
  }

  return `${englishName} (${code})`;
}

/**
 * Second line: native language name (endonym), with optional Fluent override.
 */
export function formatCrossrefLanguageNativeLabel(
  code: string,
  nativeName: string,
  translate: (key: string, defaultMessage?: string) => string | undefined
): string {
  const iso1 = iso6393To1[code];
  if (iso1) {
    const legacyKey = `${LEGACY_I18N_PREFIX}.${iso1}.native`;
    const translated = translate(legacyKey, undefined);
    if (translated) {
      return translated;
    }
    if (ISO6391.validate(iso1)) {
      return ISO6391.getNativeName(iso1);
    }
  }

  const crossrefKey = `crossref_language_codes.${code}.native`;
  const translated = translate(crossrefKey, undefined);
  if (translated) {
    return translated;
  }

  return nativeName;
}

export function toLanguageListItems(
  options: CrossrefLanguageOption[],
  translate: (key: string, defaultMessage?: string) => string | undefined
): CrossrefLanguageListItem[] {
  return options.map((option) => ({
    ...option,
    label: formatCrossrefLanguageLabel(option.code, option.name, translate),
    nativeLabel: formatCrossrefLanguageNativeLabel(
      option.code,
      option.nativeName,
      translate
    ),
  }));
}

export const crossrefLanguageCodeSet = new Set(crossrefIso6393Codes);

const iso6393NameByCode = new Map(
  iso6393.map((lang) => [lang.iso6393, lang.name])
);

const iso6391ToCrossrefCode = new Map<string, string>();
for (const lang of iso6393) {
  if (lang.iso6391 && crossrefLanguageCodeSet.has(lang.iso6393)) {
    iso6391ToCrossrefCode.set(lang.iso6391, lang.iso6393);
  }
}

const languageOptions: CrossrefLanguageOption[] = crossrefIso6393Codes.map(
  (code) => {
    const name = iso6393NameByCode.get(code) ?? code;
    const iso1 = iso6393To1[code];
    const nativeName =
      iso1 && ISO6391.validate(iso1) ? ISO6391.getNativeName(iso1) : name;
    return { code, name, nativeName };
  }
);

export function getCrossrefLanguageCodes(): readonly string[] {
  return crossrefIso6393Codes;
}

export function getLanguageByCode(
  code: string | undefined
): CrossrefLanguageOption | undefined {
  if (!code) {
    return undefined;
  }
  const normalized = code.toLowerCase();
  return languageOptions.find((item) => item.code === normalized);
}

/** True when the form field holds a non-empty language string. */
export function isNonEmptyLanguageString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/** One autocomplete row (label + native name) for a code, or null if unknown / empty. */
export function toListItemForCode(
  code: string | undefined,
  translate: (key: string, defaultMessage?: string) => string | undefined
): CrossrefLanguageListItem | null {
  if (!code) {
    return null;
  }
  const crossrefCode = normalizeCrossrefLanguageCode(code) ?? code;
  const option = getLanguageByCode(crossrefCode);
  if (!option) {
    return null;
  }
  return toLanguageListItems([option], translate)[0] ?? null;
}

/**
 * Converts ISO 639-1 (2-letter) or BCP47 tags to a Crossref XSD 3-letter code when possible.
 * Allowed 3-letter codes are exactly those in languages5.4.0.xsd (see crossrefLanguageCodeSet).
 */
export function normalizeCrossrefLanguageCode(
  code: string | undefined
): string | undefined {
  if (!code) {
    return undefined;
  }
  const normalized = code.toLowerCase().trim();
  if (crossrefLanguageCodeSet.has(normalized)) {
    return normalized;
  }
  const iso6391 = normalized.split('-')[0];
  if (iso6391.length === 2) {
    return iso6391ToCrossrefCode.get(iso6391);
  }
  return undefined;
}

/** @deprecated Prefer normalizeCrossrefLanguageCode — same behavior. */
export const convertIso6391ToCrossref6393 = normalizeCrossrefLanguageCode;

// --- Upgrade legacy form / XML values (en → eng, etc.) ------------------------

// Every `language` key in our form data is a Crossref language code.
function migrateLanguageFields(node: unknown): void {
  if (Array.isArray(node)) {
    node.forEach(migrateLanguageFields);
    return;
  }
  if (!node || typeof node !== 'object') {
    return;
  }
  const record = node as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    if (key === 'language' && typeof value === 'string') {
      const converted = normalizeCrossrefLanguageCode(value);
      if (converted) {
        record[key] = converted;
      }
    } else {
      migrateLanguageFields(value);
    }
  }
}

/**
 * Upgrades stored ISO 639-1 language values to Crossref 3-letter codes in form data.
 */
export function migrateFormDataLanguageCodes<T>(formData: T): T {
  if (!formData || typeof formData !== 'object') {
    return formData;
  }
  migrateLanguageFields(formData);
  return formData;
}

// --- Autocomplete search ----------------------------------------------------

/** Higher score = more relevant; avoids substring false positives (e.g. "spa" in "Coatzospan"). */
function scoreLanguageMatch(
  option: CrossrefLanguageOption,
  query: string
): number {
  const code = option.code;
  const name = option.name.toLowerCase();
  const native = option.nativeName.toLowerCase();

  if (code === query) {
    return 100;
  }
  if (code.startsWith(query)) {
    return 90;
  }
  if (name.startsWith(query)) {
    return 80;
  }
  if (native.startsWith(query)) {
    return 75;
  }
  if (name.split(/[\s-]+/).some((word) => word.startsWith(query))) {
    return 70;
  }
  if (code.includes(query)) {
    return 50;
  }
  if (name.includes(query)) {
    return 40;
  }
  if (native.includes(query)) {
    return 30;
  }
  return 0;
}

export function searchCrossrefLanguages(
  query: string,
  limit = CROSSREF_LANGUAGE_SUGGESTION_LIMIT
): CrossrefLanguageOption[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return [];
  }

  return languageOptions
    .map((option) => ({ option, score: scoreLanguageMatch(option, q) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || a.option.code.localeCompare(b.option.code))
    .slice(0, limit)
    .map(({ option }) => option);
}
